import React from 'react';
import { Navigate } from 'react-router-dom';
import { redirectUnlessInventory } from '../../utils/moduleAccess';
import { Form, FormGroup, FormControl, Button, Row, Col, Table, Modal } from 'react-bootstrap';
import Select from 'react-select';
import config from '../../../config';
import moment from 'moment';
import { Chart } from 'chart.js';
import { handleFocusInScrollField } from './invKeyboardScroll';
import DatacoLoadingIndicator from '../../_components/DatacoLoadingIndicator';
import {
  ensureSharedTableStyles,
  INVENTORY_STATUS_OVERVIEW_TABLE_WRAP_CLASS,
  SHARED_TABLE_SCROLL_WRAP_CLASS,
  SHARED_TABLE_ROW_SELECTED_CLASS,
} from '../../_components/Shared/dataTableStyles';

/** Overview report header (warm yellow — distinct from generic grey table headers). */
const INV_OVERVIEW_HEADER_BG = '#fff3cd';

/** Sticky column widths: No. + Part No + Description stay fixed when scrolling. */
const INV_OVERVIEW_STICKY_NO_W = 56;
const INV_OVERVIEW_STICKY_PART_W = 132;
const INV_OVERVIEW_STICKY_DESC_W = 220;

/** Solid backgrounds only — semi-transparent stripes let scrolled cells show through sticky cols. */
const invOverviewRowStripeBg = (rowIndex) => (rowIndex % 2 === 0 ? '#f8f9fa' : '#ffffff');

const invOverviewStickyNo = (backgroundColor, { header = false, footer = false } = {}) => ({
    position: 'sticky',
    left: 0,
    ...(header ? { top: 0 } : {}),
    zIndex: header ? 53 : footer ? 36 : 34,
    minWidth: INV_OVERVIEW_STICKY_NO_W,
    width: INV_OVERVIEW_STICKY_NO_W,
    backgroundColor,
    boxSizing: 'border-box',
    verticalAlign: 'middle',
    borderRight: '1px solid #dee2e6',
});

const invOverviewStickyPart = (backgroundColor, { header = false, footer = false } = {}) => ({
    position: 'sticky',
    left: INV_OVERVIEW_STICKY_NO_W,
    ...(header ? { top: 0 } : {}),
    zIndex: header ? 52 : footer ? 35 : 33,
    minWidth: INV_OVERVIEW_STICKY_PART_W,
    width: INV_OVERVIEW_STICKY_PART_W,
    maxWidth: 320,
    backgroundColor,
    boxSizing: 'border-box',
    verticalAlign: 'middle',
    borderRight: '1px solid #dee2e6',
});

const invOverviewStickyDesc = (backgroundColor, { header = false, footer = false } = {}) => ({
    position: 'sticky',
    left: INV_OVERVIEW_STICKY_NO_W + INV_OVERVIEW_STICKY_PART_W,
    ...(header ? { top: 0 } : {}),
    zIndex: header ? 51 : footer ? 34 : 32,
    minWidth: INV_OVERVIEW_STICKY_DESC_W,
    width: INV_OVERVIEW_STICKY_DESC_W,
    maxWidth: 420,
    backgroundColor,
    boxSizing: 'border-box',
    boxShadow: '10px 0 16px -8px rgba(0, 0, 0, 0.28)',
    verticalAlign: 'middle',
    borderRight: '1px solid #dee2e6',
});

/** Scrolling cells paint under sticky columns (z-index stack). */
const invOverviewScrollZ = { position: 'relative', zIndex: 0 };

/** Row 1 header cells (Quantity, totals, status) — stay above body when scrolling. */
const invOverviewHeaderRow1Cell = (extra = {}) => ({
    position: 'sticky',
    top: 0,
    zIndex: 45,
    backgroundColor: INV_OVERVIEW_HEADER_BG,
    boxShadow: '0 1px 0 #dee2e6',
    verticalAlign: 'middle',
    ...extra,
});

/** Column order: Receiving → Production → RM Whse → FG Whse → FG Delivery. */
const overviewAreaSortKey = (area) => {
    const lower = String(area || 'Unknown').trim().toLowerCase();
    if (lower === 'receiving') return 0;
    if (lower === 'production') return 1;
    if (lower === 'rm warehouse' || lower === 'rm whse' || lower === 'rm') return 2;
    if (lower === 'fg warehouse' || lower === 'fg whse' || lower === 'fg') return 3;
    if (lower === 'fg delivery') return 4;
    return 100;
};

const compareOverviewLocationCodes = (a, b) =>
    String(a || '').localeCompare(String(b || ''), undefined, { numeric: true, sensitivity: 'base' });

const sortOverviewLocations = (locations) =>
    [...locations].sort((a, b) => {
        const areaDiff = overviewAreaSortKey(a.area) - overviewAreaSortKey(b.area);
        if (areaDiff !== 0) return areaDiff;
        return compareOverviewLocationCodes(a.locationCode, b.locationCode);
    });

const sortOverviewAreas = (areas) =>
    [...areas].sort((a, b) => overviewAreaSortKey(a) - overviewAreaSortKey(b));

const formatOverviewQuantity = (n) => {
    const num = Number(n);
    if (!Number.isFinite(num) || num <= 0) return null;
    return num.toLocaleString();
};

const formatOverviewValueMyr = (n) => {
    const num = Number(n);
    if (!Number.isFinite(num) || num <= 0) return null;
    return num.toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/** Row 2 location code headers — offset below measured row 1 height. */
const invOverviewLocationHeaderCell = (extra = {}) => ({
    position: 'sticky',
    top: 'var(--inv-overview-head-row1-h, 72px)',
    zIndex: 44,
    backgroundColor: INV_OVERVIEW_HEADER_BG,
    color: '#212529',
    boxShadow: '0 1px 0 #dee2e6',
    textAlign: 'center',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    ...extra,
});

class InventoryStatusOverview extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            data: [],
            allLocations: [],
            availableAreas: [],
            loading: false,
            company: 0,
            searchPartNo: '',
            selectedAreas: [],
            selectedLocationCodes: [],
            searchStatus: '',
            timePeriod: '',
            reportDate: moment().format('DD/MM/YYYY'),
            reportTime: moment().format('HH:mm:ss'),
            showHistoryModal: false,
            historyLoading: false,
            historyPartNo: '',
            historyLocationCode: '',
            historyLocationCodes: [],
            historyScopeLabel: '',
            historyRows: [],
            historyFinalQty: 0,
            selectedOverviewRowKey: null,
            selectedHistoryRowIndex: null,
            reportShown: false,
            locations: [],
            locationTotals: {},
            locationValueTotals: {},
            totalInventory: 0,
            totalInventoryValue: 0,
            measureType: 'quantity',
        };
        this.inventoryChart = null;
        this.overviewTableWrapRef = React.createRef();
    }

    syncOverviewHeaderRowHeight = () => {
        const wrap = this.overviewTableWrapRef.current;
        if (!wrap) return;
        const row1 = wrap.querySelector('thead tr:first-child');
        if (row1) {
            wrap.style.setProperty('--inv-overview-head-row1-h', `${Math.ceil(row1.getBoundingClientRect().height)}px`);
        }
    };

    normalizeTransferStatus = (status) =>
        String(status || '')
            .toLowerCase()
            .trim()
            .replace(/\s+/g, '_');

    isScrapTransfer = (item) => {
        if (!item) return false;
        const to = String(item.toLocationCode || item.to_location_code || '').trim();
        const toDesc = String(item.toLocationDescription || item.to_location_description || '')
            .trim()
            .toUpperCase();
        const job = String(item.jobOrderNo || item.job_order_no || '')
            .trim()
            .toUpperCase();
        const marker = String(item.specialRequirement || item.special_requirement || '').toUpperCase();
        return (
            to === '__SCRAP__' ||
            job === 'SCRAP' ||
            marker.includes('[SCRAP]') ||
            toDesc.includes('SCRAP / VARIANCE')
        );
    };

    /** Pending scrap is not inventory movement until approved. */
    shouldShowTransferInHistory = (item) => {
        if (!this.isScrapTransfer(item)) return true;
        return this.normalizeTransferStatus(item.status) === 'confirmed';
    };

    /** After mapping — catch scrap rows even if typed as RM/FG Transfer. */
    shouldShowHistoryRow = (row) => {
        const to = String(row.toLocation || '').trim();
        const tt = String(row.transactionType || '');
        const isScrapRow =
            to === '__SCRAP__' ||
            tt === 'Scrap' ||
            /scrap\s*\/\s*variance/i.test(String(row.toLocation || ''));
        if (!isScrapRow) return true;
        return this.normalizeTransferStatus(row._transferStatus) === 'confirmed';
    };

    getRmHistoryType = (item) => {
        if (this.isScrapTransfer(item)) return 'Scrap';
        const isReturn = item && (item.returnIndicator === true || item.returnIndicator === 1);
        const marker = String((item && item.specialRequirement) || '').toUpperCase();
        const toDesc = String((item && item.toLocationDescription) || '').toUpperCase();
        const desc = String((item && item.description) || '').toUpperCase();
        const isUsage =
            marker.includes('AUTO_USED_FOR_PRODUCTION') ||
            toDesc.includes('USED FOR PRODUCTION') ||
            desc.includes('AUTO RM USAGE FOR FG TRANSFER');
        if (isUsage) return 'RM Usage for Production';
        if (isReturn) return 'RM Return';
        return 'RM Transfer';
    };

    getFgHistoryType = (item) => {
        if (this.isScrapTransfer(item)) return 'Scrap';
        return 'FG Transfer';
    };

    openPartHistory = async (partNo, locationScope = '') => {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        if (!user?.accountId || !partNo) return;
        const targetLocations = Array.isArray(locationScope)
            ? locationScope.map((code) => String(code || '').trim()).filter(Boolean)
            : [String(locationScope || '').trim()].filter(Boolean);
        const locationSet = new Set(targetLocations);
        const hasLocationScope = targetLocations.length > 0;
        const historyScopeLabel = hasLocationScope
            ? targetLocations.length === 1
                ? `Location: ${targetLocations[0]}`
                : `Locations: ${targetLocations.join(', ')}`
            : '';

        this.setState({
            showHistoryModal: true,
            historyLoading: true,
            historyPartNo: partNo,
            historyLocationCode: targetLocations[0] || '',
            historyLocationCodes: targetLocations,
            historyScopeLabel,
            historyRows: [],
            historyFinalQty: 0,
            selectedHistoryRowIndex: null,
        });

        const requestOptions = {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        };

        try {
            const encodedPartNo = encodeURIComponent(partNo);
            const [receivingRes, rmRes, fgTransferRes, fgDeliveryRes] = await Promise.all([
                fetch(`${config.apiUrl}/getReceiving?company=${user.accountId}&partNo=${encodedPartNo}`, requestOptions),
                fetch(
                    `${config.apiUrl}/getRMMaterialTransfers?company=${user.accountId}&partNo=${encodedPartNo}&excludePendingScrap=1`,
                    requestOptions
                ),
                fetch(
                    `${config.apiUrl}/getFGMaterialTransfers?company=${user.accountId}&partNo=${encodedPartNo}&excludePendingScrap=1`,
                    requestOptions
                ),
                fetch(`${config.apiUrl}/getFGDeliveries?company=${user.accountId}&partNo=${encodedPartNo}`, requestOptions)
            ]);

            const [receivingData, rmData, fgTransferData, fgDeliveryData] = await Promise.all([
                receivingRes.json(),
                rmRes.json(),
                fgTransferRes.json(),
                fgDeliveryRes.json()
            ]);
            const fgTransferIds = (Array.isArray(fgTransferData) ? fgTransferData : [])
                .map((row) => Number(row.id))
                .filter((id) => Number.isFinite(id));

            let fgUsageRows = [];
            if (fgTransferIds.length > 0) {
                try {
                    const usageRes = await fetch(
                        `${config.apiUrl}/getRMMaterialTransfers?company=${user.accountId}&specialRequirement=${encodeURIComponent('AUTO_USED_FOR_PRODUCTION_FG_TRANSFER:')}`,
                        requestOptions
                    );
                    const usageData = await usageRes.json();
                    const idSet = new Set(fgTransferIds.map((id) => String(id)));
                    const rmTransferIds = new Set(
                        (Array.isArray(rmData) ? rmData : []).map((row) => row.id)
                    );
                    fgUsageRows = (Array.isArray(usageData) ? usageData : [])
                        .filter((row) => {
                            const marker = String(row.specialRequirement || '');
                            const m = marker.match(/AUTO_USED_FOR_PRODUCTION_FG_TRANSFER:(\d+):/i);
                            return !!(m && idSet.has(String(m[1])));
                        })
                        .filter((row) => this.shouldShowTransferInHistory(row))
                        // Same RM transfer is already in rmData as "RM Usage for Production" — skip duplicate ledger line
                        .filter((row) => !rmTransferIds.has(row.id));
                } catch (usageErr) {
                    console.error('Error loading FG-linked RM usage rows:', usageErr);
                }
            }

            const allRows = [
                ...(Array.isArray(receivingData) ? receivingData : []).map((item) => ({
                    key: `RCV-${item.id}`,
                    transactionType: 'Receiving',
                    transactionId: item.transactionId || '-',
                    fromLocation: item.supplier || '-',
                    toLocation: item.locationCode || '-',
                    qty: Number(item.quantity || 0),
                    createdAt: item.createdAt || null,
                    dateText: item.createdAt ? moment(item.createdAt).format('DD/MM/YYYY') : '-',
                    timeText: item.createdAt ? moment(item.createdAt).format('HH:mm:ss') : '-'
                })),
                ...(Array.isArray(rmData) ? rmData : [])
                    .filter((item) => this.shouldShowTransferInHistory(item))
                    .map((item) => ({
                    key: `RM-${item.id}`,
                    transactionType: this.getRmHistoryType(item),
                    transactionId: item.transactionId || '-',
                    fromLocation: item.fromLocationCode || '-',
                    toLocation: item.toLocationCode || '-',
                    qty: Number(item.quantity || 0),
                    createdAt: item.createdAt || null,
                    dateText: item.createdAt ? moment(item.createdAt).format('DD/MM/YYYY') : '-',
                    timeText: item.createdAt ? moment(item.createdAt).format('HH:mm:ss') : '-',
                    _transferStatus: item.status
                })),
                ...(Array.isArray(fgTransferData) ? fgTransferData : [])
                    .filter((item) => this.shouldShowTransferInHistory(item))
                    .map((item) => ({
                    key: `FGT-${item.id}`,
                    transactionType: this.getFgHistoryType(item),
                    transactionId: item.transactionId || '-',
                    fromLocation: item.fromLocationCode || '-',
                    toLocation: item.toLocationCode || '-',
                    qty: Number(item.quantity || 0),
                    createdAt: item.createdAt || null,
                    dateText: item.createdAt ? moment(item.createdAt).format('DD/MM/YYYY') : '-',
                    timeText: item.createdAt ? moment(item.createdAt).format('HH:mm:ss') : '-',
                    _transferStatus: item.status
                })),
                ...(Array.isArray(fgDeliveryData) ? fgDeliveryData : []).map((item) => ({
                    key: `FGD-${item.id}`,
                    transactionType: 'FG Delivery',
                    transactionId: item.transactionId || '-',
                    fromLocation: item.fromLocationCode || '-',
                    toLocation: item.toLocationCode || '-',
                    qty: Number(item.quantity || 0),
                    createdAt: item.createdAt || null,
                    dateText: item.createdAt ? moment(item.createdAt).format('DD/MM/YYYY') : '-',
                    timeText: item.createdAt ? moment(item.createdAt).format('HH:mm:ss') : '-'
                })),
                ...fgUsageRows.map((item) => ({
                    key: `FG-RM-USAGE-${item.id}`,
                    transactionType: `FG Production Usage (RM ${item.partNo || '-'})`,
                    transactionId: item.transactionId || '-',
                    fromLocation: item.fromLocationCode || '-',
                    toLocation: item.toLocationCode || '-',
                    qty: Number(item.quantity || 0),
                    createdAt: item.createdAt || null,
                    dateText: item.createdAt ? moment(item.createdAt).format('DD/MM/YYYY') : '-',
                    timeText: item.createdAt ? moment(item.createdAt).format('HH:mm:ss') : '-',
                    _transferStatus: item.status
                }))
            ].filter((row) => this.shouldShowHistoryRow(row));

            const relevantRows = allRows.filter((row) => {
                if (!hasLocationScope) return true;
                return locationSet.has(row.fromLocation) || locationSet.has(row.toLocation);
            });

            const ascendingRows = [...relevantRows].sort(
                (a, b) => moment(a.createdAt).valueOf() - moment(b.createdAt).valueOf()
            );

            let runningQty = 0;
            const ledgerRows = ascendingRows.map((row, index) => {
                const qty = Number(row.qty || 0);
                const tt = String(row.transactionType || '');
                const toLoc = String(row.toLocation || '').trim();
                /** RM consumed for production: stock leaves from-location only — never count as In at to-location. */
                const isRmProductionUsage =
                    tt === 'RM Usage for Production' || tt.startsWith('FG Production Usage');
                const isScrapOut = tt === 'Scrap' || toLoc === '__SCRAP__';
                let inQty = 0;
                let outQty = 0;
                if (hasLocationScope) {
                    if (isRmProductionUsage || isScrapOut) {
                        outQty = locationSet.has(row.fromLocation) ? qty : 0;
                    } else {
                        inQty = locationSet.has(row.toLocation) ? qty : 0;
                        outQty = locationSet.has(row.fromLocation) ? qty : 0;
                    }
                }
                runningQty += inQty - outQty;
                return {
                    ...row,
                    key: `${row.key}-${index}`,
                    inQty,
                    outQty,
                    runningQty
                };
            });

            const historyRows = hasLocationScope
                ? ledgerRows
                : [...allRows].sort((a, b) => moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf());
            const historyFinalQty = hasLocationScope ? runningQty : 0;

            this.setState({ historyRows, historyLoading: false, historyFinalQty });
        } catch (error) {
            console.error('Error loading part transaction history:', error);
            this.setState({ historyRows: [], historyLoading: false, historyFinalQty: 0 });
        }
    }

    closePartHistory = () => {
        this.setState({
            showHistoryModal: false,
            historyPartNo: '',
            historyLocationCode: '',
            historyLocationCodes: [],
            historyScopeLabel: '',
            historyRows: [],
            historyLoading: false,
            historyFinalQty: 0,
            selectedHistoryRowIndex: null,
        });
    }

    componentDidMount() {
        ensureSharedTableStyles();
        this.loadUserData();
        // Draw chart after a short delay to ensure canvas is rendered
        setTimeout(() => {
            this.drawBarChart();
            this.syncOverviewHeaderRowHeight();
        }, 100);
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.company !== this.state.company && this.state.company) {
            this.loadLocations();
        }
        // Update chart when data or loading state changes
        if (
            this.state.reportShown &&
            (prevState.data !== this.state.data ||
                prevState.loading !== this.state.loading ||
                prevState.measureType !== this.state.measureType) &&
            !this.state.loading
        ) {
            setTimeout(() => {
                this.drawBarChart();
            }, 100);
        }
        if (
            prevState.data !== this.state.data ||
            prevState.loading !== this.state.loading ||
            prevState.selectedLocationCodes !== this.state.selectedLocationCodes ||
            prevState.selectedAreas !== this.state.selectedAreas
        ) {
            setTimeout(() => this.syncOverviewHeaderRowHeight(), 0);
        }
    }

    componentWillUnmount() {
        if (this.inventoryChart) {
            this.inventoryChart.destroy();
        }
    }

    loadUserData = () => {
        try {
            const userData = JSON.parse(localStorage.getItem('user'));
            if (userData && userData.accountId) {
                this.setState({ company: userData.accountId }, () => {
                    this.loadLocations();
                });
            }
        } catch (error) {
            console.error('Error loading user data:', error);
        }
    }

    loadLocations = () => {
        const user = JSON.parse(localStorage.getItem('user'));
        if (!user || !user.accountId) {
            return;
        }

        fetch(`${config.apiUrl}/getMasterLocations?company=${user.accountId}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        })
            .then(response => response.json())
            .then(data => {
                const locations = Array.isArray(data) ? data : [];
                const areas = [...new Set(locations.map(loc => loc.area).filter(Boolean))].sort();
                const nextState = {
                    allLocations: locations,
                    availableAreas: areas
                };
                if (locations.length === 1 && locations[0].locationCode) {
                    nextState.selectedAreas = locations[0].area ? [locations[0].area] : [];
                    nextState.selectedLocationCodes = [locations[0].locationCode];
                }
                this.setState(nextState);
            })
            .catch(error => {
                console.error('Error loading locations:', error);
                this.setState({ allLocations: [], availableAreas: [] });
            });
    }

    clearReportResults = () => {
        this.setState({
            data: [],
            locations: [],
            locationTotals: {},
            locationValueTotals: {},
            totalInventory: 0,
            totalInventoryValue: 0,
            reportShown: false,
            selectedOverviewRowKey: null,
            loading: false,
        });
    };

    loadReportData = () => {
        const user = JSON.parse(localStorage.getItem('user'));
        if (!user || !user.accountId) {
            return;
        }

        this.setState({ loading: true, reportShown: true });

        let url = `${config.apiUrl}/getInventoryStatusOverview?company=${user.accountId}`;
        
        if (this.state.searchPartNo) {
            url += `&partNo=${encodeURIComponent(this.state.searchPartNo)}`;
        }

        if (this.state.selectedAreas && this.state.selectedAreas.length > 0) {
            url += `&area=${encodeURIComponent(this.state.selectedAreas.join(','))}`;
        }

        if (this.state.selectedLocationCodes && this.state.selectedLocationCodes.length > 0) {
            url += `&locationCodes=${encodeURIComponent(this.state.selectedLocationCodes.join(','))}`;
        }
        
        if (this.state.searchStatus) {
            url += `&status=${encodeURIComponent(this.state.searchStatus)}`;
        }
        
        if (this.state.timePeriod) {
            url += `&timePeriod=${encodeURIComponent(this.state.timePeriod)}`;
        }

        fetch(url, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        })
            .then(response => response.json())
            .then(data => {
                this.setState({
                    data: data.parts || [],
                    locations: data.locations || [],
                    locationTotals: data.locationTotals || {},
                    locationValueTotals: data.locationValueTotals || {},
                    totalInventory: data.totalInventory || 0,
                    totalInventoryValue: data.totalInventoryValue || 0,
                    loading: false,
                    reportDate: moment().format('DD/MM/YYYY'),
                    reportTime: moment().format('HH:mm:ss')
                });
            })
            .catch(error => {
                console.error('Error loading report data:', error);
                this.setState({ 
                    data: [], 
                    locations: [],
                    locationTotals: {},
                    locationValueTotals: {},
                    totalInventory: 0,
                    totalInventoryValue: 0,
                    loading: false 
                });
            });
    }

    /** Paste / IME can skip `change` on some browsers; `input` + post-paste tick cover controlled updates. */
    handlePartNoChange = (e) => {
        const el = e?.currentTarget ?? e?.target;
        const v = el && typeof el.value === 'string' ? el.value : '';
        this.setState({ searchPartNo: v });
    };

    handlePartNoPaste = (e) => {
        const el = e?.currentTarget ?? e?.target;
        if (!el) return;
        const apply = () => {
            const v = typeof el.value === 'string' ? el.value : '';
            this.setState({ searchPartNo: v });
        };
        requestAnimationFrame(apply);
        setTimeout(apply, 0);
    };

    handleAreaSelectChange = (options) => {
        const selectedAreas = (options || []).map((o) => o.value).filter(Boolean);
        const { allLocations, selectedLocationCodes } = this.state;
        let nextLocationCodes = selectedLocationCodes;
        if (selectedAreas.length > 0 && Array.isArray(allLocations)) {
            const allowed = new Set(
                allLocations.filter((loc) => selectedAreas.includes(loc.area)).map((l) => l.locationCode)
            );
            nextLocationCodes = selectedLocationCodes.filter((code) => allowed.has(code));
        }

        this.setState({
            selectedAreas,
            selectedLocationCodes: nextLocationCodes,
        });
    }

    handleLocationSelectChange = (options) => {
        const selectedLocationCodes = (options || []).map((o) => o.locationCode).filter(Boolean);
        this.setState({ selectedLocationCodes });
    }

    handleStatusChange = (e) => {
        this.setState({ searchStatus: e.target.value });
    }

    handleTimePeriodChange = (e) => {
        this.setState({ timePeriod: e.target.value });
    }

    handleMeasureTypeChange = (e) => {
        this.setState({ measureType: e.target.value }, () => {
            setTimeout(() => this.drawBarChart(), 0);
        });
    }

    isValueMeasure = () => this.state.measureType === 'value';

    formatOverviewMetric = (n) =>
        this.isValueMeasure() ? formatOverviewValueMyr(n) : formatOverviewQuantity(n);

    getLocationMetric = (part, locCode) => {
        const ld = part.locations && part.locations[locCode];
        if (!ld) return 0;
        if (this.isValueMeasure()) {
            return Number(ld.value) || 0;
        }
        return Number(ld.quantity) || 0;
    };

    getPartTotalMetric = (part) =>
        this.isValueMeasure()
            ? Number(part.totalInventoryValue) || 0
            : Number(part.totalInventory) || 0;

    getLocationTotalMetric = (locCode) =>
        this.isValueMeasure()
            ? Number(this.state.locationValueTotals[locCode]) || 0
            : Number(this.state.locationTotals[locCode]) || 0;

    handleSearch = () => {
        this.loadReportData();
    }

    handleReset = () => {
        this.setState({
            searchPartNo: '',
            selectedAreas: [],
            selectedLocationCodes: [],
            searchStatus: '',
            timePeriod: '',
        }, this.clearReportResults);
    }

    getStatusColor = (status) => {
        const statusLower = (status || '').toLowerCase();
        if (statusLower === 'reject' || statusLower === 'rejected') {
            return '#FF6B6B'; // Red
        } else if (statusLower === 'on_hold' || statusLower === 'onhold' || statusLower === 'on hold') {
            return '#FFA500'; // Orange
        } else {
            return '#90EE90'; // Green
        }
    }

    getStatusText = (status) => {
        const statusLower = (status || '').toLowerCase();
        if (statusLower === 'reject' || statusLower === 'rejected') {
            return 'Reject';
        } else if (statusLower === 'on_hold' || statusLower === 'onhold' || statusLower === 'on hold') {
            return 'On Hold';
        } else {
            return 'Released';
        }
    }

    drawBarChart = () => {
        const canvas = document.getElementById('inventoryBarChart');
        if (!canvas || this.state.loading || !this.state.data || this.state.data.length === 0) {
            if (this.inventoryChart) {
                this.inventoryChart.destroy();
                this.inventoryChart = null;
            }
            const wrap = document.getElementById('inventoryBarChartWrap');
            if (wrap) wrap.style.height = '520px';
            return;
        }

        const { data, locations, measureType } = this.state;
        const valueMode = measureType === 'value';

        const reportLocations = (locations && locations.length > 0) ? locations :
            (data.length > 0 && data[0].locations ?
                Object.values(data[0].locations).map(loc => ({
                    locationCode: loc.locationCode,
                    area: loc.area
                })) : []);

        if (reportLocations.length === 0) {
            if (this.inventoryChart) {
                this.inventoryChart.destroy();
                this.inventoryChart = null;
            }
            const wrap = document.getElementById('inventoryBarChartWrap');
            if (wrap) wrap.style.height = '520px';
            return;
        }

        // Bar chart: X-axis = location + part no., Y-axis = quantity (vertical bars)
        const flatLocs = sortOverviewLocations(reportLocations);
        const sortedParts = [...data].sort((a, b) =>
            String(a.partNo || '').localeCompare(String(b.partNo || ''))
        );

        const colorPalette = [
            '#3498db',
            '#e74c3c',
            '#2ecc71',
            '#f39c12',
            '#9b59b6',
            '#1abc9c',
            '#34495e',
            '#e67e22',
            '#95a5a6',
            '#d35400'
        ];
        const partColorIndex = {};
        sortedParts.forEach((p, i) => {
            partColorIndex[p.partNo] = i % colorPalette.length;
        });

        const rows = [];
        flatLocs.forEach((loc) => {
            sortedParts.forEach((part) => {
                const locationData = part.locations && part.locations[loc.locationCode];
                const metric = valueMode
                    ? (locationData ? Number(locationData.value) : 0)
                    : (locationData ? Number(locationData.quantity) : 0);
                if (!Number.isFinite(metric) || metric <= 0) return;
                rows.push({
                    label: `${loc.area || 'Unknown'}: ${loc.locationCode} · ${part.partNo}`,
                    qty: metric,
                    partNo: part.partNo
                });
            });
        });

        const wrap = document.getElementById('inventoryBarChartWrap');
        if (rows.length === 0) {
            if (this.inventoryChart) {
                this.inventoryChart.destroy();
                this.inventoryChart = null;
            }
            if (wrap) wrap.style.height = '520px';
            return;
        }

        const labels = rows.map((r) => r.label);
        const quantities = rows.map((r) => r.qty);
        const backgroundColor = rows.map((r) => colorPalette[partColorIndex[r.partNo]]);
        const borderColor = backgroundColor;

        if (wrap) {
            wrap.style.height = `${Math.max(480, Math.min(720, 400 + Math.min(labels.length, 40) * 6))}px`;
        }

        if (this.inventoryChart) {
            this.inventoryChart.destroy();
        }

        const ctx = canvas.getContext('2d');
        this.inventoryChart = new Chart(ctx, {
            type: 'bar',
            data: {
                labels,
                datasets: [
                    {
                        label: valueMode ? 'Value (MYR)' : 'Quantity',
                        data: quantities,
                        backgroundColor,
                        borderColor,
                        borderWidth: 1
                    }
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    xAxes: [
                        {
                            scaleLabel: {
                                display: true,
                                labelString: 'Location · Part No.',
                                fontStyle: 'bold'
                            },
                            ticks: {
                                autoSkip: labels.length > 24,
                                maxRotation: labels.length > 4 ? 60 : 0,
                                minRotation: labels.length > 4 ? 45 : 0
                            }
                        }
                    ],
                    yAxes: [
                        {
                            beginAtZero: true,
                            scaleLabel: {
                                display: true,
                                labelString: valueMode ? 'Value (MYR)' : 'Quantity',
                                fontStyle: 'bold'
                            },
                            ticks: {
                                callback: function (value) {
                                    if (isNaN(value) || !isFinite(value)) return '';
                                    if (valueMode) {
                                        return Number(value).toLocaleString('en-MY', {
                                            minimumFractionDigits: 0,
                                            maximumFractionDigits: 0,
                                        });
                                    }
                                    return value.toLocaleString();
                                }
                            }
                        }
                    ]
                },
                legend: {
                    display: false
                },
                title: {
                    display: true,
                    text: valueMode ? 'Inventory Status Overview — Value (MYR)' : 'Inventory Status Overview',
                    fontSize: 16,
                    fontStyle: 'bold'
                },
                tooltips: {
                    callbacks: {
                        title: function (tooltipItems, chartData) {
                            const idx = tooltipItems[0] && tooltipItems[0].index;
                            return idx != null ? chartData.labels[idx] : '';
                        },
                        label: function (tooltipItem, chartData) {
                            const value = tooltipItem.yLabel;
                            const n = Number(value);
                            if (valueMode) {
                                return `Value (MYR): ${Number.isFinite(n) ? n.toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : String(value)}`;
                            }
                            return `Quantity: ${Number.isFinite(n) ? n.toLocaleString() : String(value)}`;
                        },
                        afterLabel: function () {
                            return '';
                        }
                    }
                },
                // chartjs-plugin-labels (imported globally) defaults to percentage — show quantity on bars
                plugins: {
                    labels: {
                        render: function (args) {
                            const n = Number(args.value);
                            if (!Number.isFinite(n)) return '';
                            if (valueMode) {
                                return n.toLocaleString('en-MY', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
                            }
                            return n.toLocaleString();
                        },
                        fontSize: 11,
                        fontColor: '#212529',
                        fontStyle: 'bold',
                        showZero: true
                    }
                }
            }
        });
    }

    render() {
        const accessDenied = redirectUnlessInventory(localStorage.getItem('user'));
        if (accessDenied) return accessDenied;

        const { 
            data, 
            locations, 
            locationTotals,
            locationValueTotals,
            totalInventory,
            totalInventoryValue,
            measureType,
            loading,
            searchPartNo,
            selectedAreas,
            selectedLocationCodes,
            searchStatus,
            timePeriod,
            allLocations,
            availableAreas,
            reportDate,
            reportTime,
            showHistoryModal,
            historyLoading,
            historyPartNo,
            historyLocationCode,
            historyLocationCodes,
            historyScopeLabel,
            historyRows,
            historyFinalQty,
            reportShown,
        } = this.state;
        const hasHistoryLocationScope = historyLocationCodes.length > 0;
        const valueMode = measureType === 'value';

        // Group locations by area for display
        const locationsByArea = {};
        allLocations.forEach(loc => {
            if (!locationsByArea[loc.area]) {
                locationsByArea[loc.area] = [];
            }
            locationsByArea[loc.area].push(loc);
        });

        // Filter locations to show only selected ones in the report
        // If no locations selected, show all locations from the data
        const reportLocations = sortOverviewLocations(
            (locations && locations.length > 0) ? locations :
            (data.length > 0 && data[0].locations ?
                Object.values(data[0].locations).map(loc => ({
                    locationCode: loc.locationCode,
                    area: loc.area
                })) : [])
        );

        const areaCategories = sortOverviewAreas(
            [...new Set(reportLocations.map((l) => l.area || 'Unknown'))]
        );

        // Location code options for multi-select: union of codes in selected areas, or all when none selected
        let locationSelectOptions = [];
        if (selectedAreas.length > 0) {
            const seen = new Set();
            selectedAreas.forEach((area) => {
                (locationsByArea[area] || []).forEach((loc) => {
                    if (loc.locationCode && !seen.has(loc.locationCode)) {
                        seen.add(loc.locationCode);
                        locationSelectOptions.push(loc);
                    }
                });
            });
        } else {
            locationSelectOptions = Object.keys(locationsByArea)
                .sort()
                .flatMap((area) => (locationsByArea[area] || []).map((loc) => ({ ...loc, _groupArea: area })));
        }

        const areaFilterOptions = availableAreas.map((a) => ({ value: a, label: a }));
        const areaSelectValue = selectedAreas.map((a) => ({ value: a, label: a }));

        const locationFilterOptions = locationSelectOptions.map((loc) => {
            const areaKey = loc._groupArea || loc.area;
            const value =
                selectedAreas.length > 0
                    ? loc.locationCode
                    : `${areaKey}|||${loc.locationCode}`;
            return {
                value,
                label:
                    selectedAreas.length > 0
                        ? loc.locationCode
                        : `[${areaKey}] ${loc.locationCode}`,
                locationCode: loc.locationCode
            };
        });
        const locationSelectValue = selectedLocationCodes.map((code) => {
            const opt = locationFilterOptions.find((o) => o.locationCode === code);
            if (opt) return opt;
            return { value: code, label: code, locationCode: code };
        });

        const partTotalForArea = (part, area) =>
            reportLocations
                .filter((loc) => (loc.area || 'Unknown') === area)
                .reduce((sum, loc) => {
                    const ld = part.locations && part.locations[loc.locationCode];
                    if (!ld) return sum;
                    const n = valueMode ? Number(ld.value) : Number(ld.quantity);
                    return sum + (Number.isFinite(n) ? n : 0);
                }, 0);

        const plantTotalForArea = (area) =>
            reportLocations
                .filter((loc) => (loc.area || 'Unknown') === area)
                .reduce((sum, loc) => {
                    const t = valueMode
                        ? locationValueTotals[loc.locationCode]
                        : locationTotals[loc.locationCode];
                    const n = t != null ? Number(t) : 0;
                    return sum + (Number.isFinite(n) ? n : 0);
                }, 0);

        const formatMetric = (n) =>
            valueMode ? formatOverviewValueMyr(n) : formatOverviewQuantity(n);

        return (
            <div style={{ padding: '20px' }} onFocusCapture={handleFocusInScrollField}>
                <h2 style={{ marginBottom: '20px' }}>Inventory Status Overview Report</h2>
                
                {/* Search Filters */}
                <div style={{ 
                    marginBottom: '20px', 
                    padding: '15px', 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: '5px',
                    border: '1px solid #dee2e6'
                }}>
                    <Row>
                        <Col md={2}>
                            <FormGroup>
                                <label>Part No</label>
                                <FormControl
                                    type="text"
                                    placeholder="Enter Part No"
                                    value={searchPartNo}
                                    autoComplete="off"
                                    spellCheck={false}
                                    onChange={this.handlePartNoChange}
                                    onInput={this.handlePartNoChange}
                                    onPaste={this.handlePartNoPaste}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={3}>
                            <FormGroup>
                                <label>Area</label>
                                <Select
                                    inputId="inv-overview-area-filter"
                                    isMulti
                                    isClearable
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                    placeholder="All areas"
                                    options={areaFilterOptions}
                                    value={areaSelectValue}
                                    onChange={this.handleAreaSelectChange}
                                    className="basic-multi-select"
                                    classNamePrefix="select"
                                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                    styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        control: (base) => ({ ...base, minHeight: '38px' })
                                    }}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={3}>
                            <FormGroup>
                                <label>Location Code</label>
                                <Select
                                    inputId="inv-overview-location-filter"
                                    isMulti
                                    isClearable
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                    placeholder="All location codes"
                                    options={locationFilterOptions}
                                    value={locationSelectValue}
                                    onChange={this.handleLocationSelectChange}
                                    className="basic-multi-select"
                                    classNamePrefix="select"
                                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                    styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        control: (base) => ({ ...base, minHeight: '38px' })
                                    }}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={2}>
                            <FormGroup>
                                <label>Status</label>
                                <FormControl
                                    as="select"
                                    value={searchStatus}
                                    onChange={this.handleStatusChange}
                                >
                                    <option value="">All</option>
                                    <option value="Released">Released</option>
                                    <option value="On Hold">On Hold</option>
                                    <option value="Reject">Reject</option>
                                </FormControl>
                            </FormGroup>
                        </Col>
                        <Col md={2}>
                            <FormGroup>
                                <label>Period</label>
                                <FormControl
                                    as="select"
                                    value={timePeriod}
                                    onChange={this.handleTimePeriodChange}
                                >
                                    <option value="">All</option>
                                    <option value="Daily">Daily</option>
                                    <option value="Weekly">Weekly</option>
                                    <option value="Monthly">Monthly</option>
                                    <option value="Yearly">Yearly</option>
                                </FormControl>
                            </FormGroup>
                        </Col>
                        <Col md={2}>
                            <FormGroup>
                                <label>Display</label>
                                <FormControl
                                    as="select"
                                    value={measureType}
                                    onChange={this.handleMeasureTypeChange}
                                >
                                    <option value="quantity">Quantity</option>
                                    <option value="value">Value (MYR)</option>
                                </FormControl>
                            </FormGroup>
                        </Col>
                        <Col md={2} style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '15px' }}>
                            <Button 
                                variant="primary" 
                                onClick={this.handleSearch}
                                style={{ marginRight: '10px', width: '100%' }}
                            >
                                Search
                            </Button>
                        </Col>
                        <Col md={2} style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '15px' }}>
                            <Button 
                                variant="secondary" 
                                onClick={this.handleReset}
                                style={{ width: '100%' }}
                            >
                                Reset
                            </Button>
                        </Col>
                    </Row>
                </div>

                {!reportShown ? (
                    <div
                        style={{
                            marginTop: '24px',
                            padding: '32px 24px',
                            textAlign: 'center',
                            color: '#6c757d',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '5px',
                            border: '1px solid #dee2e6',
                        }}
                    >
                        Set filters if needed, then click <strong>Search</strong> to generate the report.
                    </div>
                ) : null}

                {reportShown ? (
                <>
                {/* Report Header */}
                <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between' }}>
                    <div>
                        <strong>Date Report Generated:</strong> {reportDate}
                    </div>
                    <div>
                        <strong>Time:</strong> {reportTime}
                    </div>
                </div>

                {/* Bar Chart */}
                {!loading && data && data.length > 0 && (
                    <div style={{ marginBottom: '30px', padding: '15px', backgroundColor: '#fff', borderRadius: '5px', border: '1px solid #dee2e6' }}>
                        <div id="inventoryBarChartWrap" style={{ minHeight: '480px', height: '560px', position: 'relative' }}>
                            <canvas id="inventoryBarChart"></canvas>
                        </div>
                    </div>
                )}

                {/* Report Table */}
                {loading ? (
                    <DatacoLoadingIndicator loading={loading} label="Loading inventory status overview" />
                ) : !data || data.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                        No inventory records match the selected filters.
                    </div>
                ) : (
                    <div
                        ref={this.overviewTableWrapRef}
                        className={INVENTORY_STATUS_OVERVIEW_TABLE_WRAP_CLASS}
                        style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}
                    >
                        <Table bordered striped hover style={{ marginBottom: 0 }}>
                            <thead>
                                <tr>
                                    <th
                                        rowSpan={2}
                                        style={{
                                            ...invOverviewStickyNo(INV_OVERVIEW_HEADER_BG, { header: true }),
                                            textAlign: 'center',
                                        }}
                                    >
                                        No
                                    </th>
                                    <th
                                        rowSpan={2}
                                        style={{
                                            ...invOverviewStickyPart(INV_OVERVIEW_HEADER_BG, { header: true }),
                                            textAlign: 'center',
                                        }}
                                    >
                                        Part No.
                                    </th>
                                    <th
                                        rowSpan={2}
                                        style={{
                                            ...invOverviewStickyDesc(INV_OVERVIEW_HEADER_BG, { header: true }),
                                            textAlign: 'center',
                                        }}
                                    >
                                        Description
                                    </th>
                                    <th
                                        colSpan={reportLocations.length}
                                        style={invOverviewHeaderRow1Cell({ textAlign: 'center' })}
                                    >
                                        {valueMode ? 'Value (MYR)' : 'Quantity'}
                                    </th>
                                    <th
                                        rowSpan={2}
                                        style={invOverviewHeaderRow1Cell({
                                            textAlign: 'center',
                                        })}
                                    >
                                        {valueMode ? (
                                            <>
                                                Total Value (MYR)
                                                <br />
                                                based on Part No.
                                            </>
                                        ) : (
                                            <>
                                                Total Inventory
                                                <br />
                                                based on Part No.
                                            </>
                                        )}
                                    </th>
                                    {areaCategories.map((area) => (
                                        <th
                                            key={`hdr-area-${area}`}
                                            rowSpan={2}
                                            style={invOverviewHeaderRow1Cell({
                                                textAlign: 'center',
                                                fontSize: '11px',
                                                whiteSpace: 'normal',
                                                maxWidth: '96px',
                                            })}
                                        >
                                            Total
                                            <br />
                                            <span style={{ fontWeight: '600' }}>{area}</span>
                                        </th>
                                    ))}
                                    <th
                                        rowSpan={2}
                                        style={invOverviewHeaderRow1Cell({ textAlign: 'center' })}
                                    >
                                        Status
                                    </th>
                                </tr>
                                <tr>
                                    {reportLocations.map((loc) => (
                                        <th
                                            key={loc.locationCode}
                                            style={invOverviewLocationHeaderCell()}
                                        >
                                            {loc.area}: {loc.locationCode}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((part, index) => (
                                    <tr
                                        key={part.partNo}
                                        className={this.state.selectedOverviewRowKey === part.partNo ? SHARED_TABLE_ROW_SELECTED_CLASS : ''}
                                        onClick={() => this.setState((s) => ({
                                            selectedOverviewRowKey: s.selectedOverviewRowKey === part.partNo ? null : part.partNo,
                                        }))}
                                    >
                                        <td
                                            style={{
                                                ...invOverviewStickyNo(invOverviewRowStripeBg(index)),
                                                textAlign: 'center',
                                            }}
                                        >
                                            {index + 1}
                                        </td>
                                        <td style={{ ...invOverviewStickyPart(invOverviewRowStripeBg(index)), textAlign: 'left' }}>
                                            <a
                                                href="#"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    this.openPartHistory(part.partNo);
                                                }}
                                                style={{ textDecoration: 'underline', cursor: 'pointer' }}
                                            >
                                                {part.partNo}
                                            </a>
                                        </td>
                                        <td style={{ ...invOverviewStickyDesc(invOverviewRowStripeBg(index)), whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                            {part.description || '-'}
                                        </td>
                                        {reportLocations.map(loc => {
                                            const metric = this.getLocationMetric(part, loc.locationCode);
                                            const locationData = part.locations[loc.locationCode];
                                            const status = locationData ? locationData.status : 'released';
                                            const bgColor = this.getStatusColor(status);
                                            const formatted = formatMetric(metric);
                                            return (
                                                <td 
                                                    key={loc.locationCode}
                                                    style={{ 
                                                        ...invOverviewScrollZ,
                                                        textAlign: 'center',
                                                        backgroundColor: bgColor,
                                                        color: '#000',
                                                        fontWeight: '500'
                                                    }}
                                                >
                                                    {formatted ? (
                                                        <a
                                                            href="#"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                this.openPartHistory(part.partNo, loc.locationCode);
                                                            }}
                                                            style={{ textDecoration: 'underline', cursor: 'pointer', color: '#0d6efd' }}
                                                            title={`Show ${part.partNo} transactions at ${loc.locationCode}`}
                                                        >
                                                            {formatted}
                                                        </a>
                                                    ) : '-'}
                                                </td>
                                            );
                                        })}
                                        <td 
                                            style={{ 
                                                ...invOverviewScrollZ,
                                                textAlign: 'center',
                                                backgroundColor: this.getStatusColor(part.overallStatus),
                                                color: '#000',
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            {(() => {
                                                const totalMetric = this.getPartTotalMetric(part);
                                                const formatted = formatMetric(totalMetric);
                                                return formatted ? (
                                                <a
                                                    href="#"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        this.openPartHistory(
                                                            part.partNo,
                                                            reportLocations.map((loc) => loc.locationCode).filter(Boolean)
                                                        );
                                                    }}
                                                    style={{ textDecoration: 'underline', cursor: 'pointer', color: '#0d6efd' }}
                                                    title={`Show ${part.partNo} transactions across all shown locations`}
                                                >
                                                    {formatted}
                                                </a>
                                            ) : '-';
                                            })()}
                                        </td>
                                        {areaCategories.map((area) => {
                                            const sub = partTotalForArea(part, area);
                                            const areaLocationCodes = reportLocations
                                                .filter((loc) => (loc.area || 'Unknown') === area)
                                                .map((loc) => loc.locationCode)
                                                .filter(Boolean);
                                            return (
                                                <td
                                                    key={`${part.partNo}-area-${area}`}
                                                    style={{
                                                        ...invOverviewScrollZ,
                                                        textAlign: 'center',
                                                        backgroundColor: '#f1f3f5',
                                                        color: '#000',
                                                        fontWeight: '600',
                                                    }}
                                                >
                                                    {(() => {
                                                        const formatted = formatMetric(sub);
                                                        return formatted ? (
                                                        <a
                                                            href="#"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                this.openPartHistory(part.partNo, areaLocationCodes);
                                                            }}
                                                            style={{ textDecoration: 'underline', cursor: 'pointer', color: '#0d6efd' }}
                                                            title={`Show ${part.partNo} transactions in ${area}`}
                                                        >
                                                            {formatted}
                                                        </a>
                                                    ) : '-';
                                                    })()}
                                                </td>
                                            );
                                        })}
                                        <td 
                                            style={{ 
                                                ...invOverviewScrollZ,
                                                textAlign: 'center',
                                                backgroundColor: this.getStatusColor(part.overallStatus),
                                                color: '#000',
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            {this.getStatusText(part.overallStatus)}
                                        </td>
                                    </tr>
                                ))}
                                {/* Total row for each location */}
                                <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                                    <td
                                        colSpan={3}
                                        style={{
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 40,
                                            textAlign: 'center',
                                            backgroundColor: '#f8f9fa',
                                            minWidth:
                                                INV_OVERVIEW_STICKY_NO_W +
                                                INV_OVERVIEW_STICKY_PART_W +
                                                INV_OVERVIEW_STICKY_DESC_W,
                                            boxShadow: '10px 0 16px -8px rgba(0, 0, 0, 0.28)',
                                            boxSizing: 'border-box',
                                            borderRight: '1px solid #dee2e6',
                                        }}
                                    >
                                        {valueMode ? 'Overall Total Value (MYR) in the Plant' : 'Overall Total Inventory in the Plant'}
                                    </td>
                                    {reportLocations.map(loc => {
                                        const formatted = formatMetric(this.getLocationTotalMetric(loc.locationCode));
                                        return (
                                        <td key={loc.locationCode} style={{ ...invOverviewScrollZ, textAlign: 'center' }}>
                                            {formatted || '-'}
                                        </td>
                                    );
                                    })}
                                    <td 
                                        style={{ 
                                            ...invOverviewScrollZ,
                                            textAlign: 'center',
                                            backgroundColor: '#90EE90',
                                            color: '#000',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        {formatMetric(valueMode ? totalInventoryValue : totalInventory) || '-'}
                                    </td>
                                    {areaCategories.map((area) => {
                                        const sub = plantTotalForArea(area);
                                        const formatted = formatMetric(sub);
                                        return (
                                            <td
                                                key={`footer-area-${area}`}
                                                style={{
                                                    ...invOverviewScrollZ,
                                                    textAlign: 'center',
                                                    backgroundColor: '#cfe2ff',
                                                    color: '#000',
                                                    fontWeight: 'bold',
                                                }}
                                            >
                                                {formatted || '-'}
                                            </td>
                                        );
                                    })}
                                    <td 
                                        style={{ 
                                            ...invOverviewScrollZ,
                                            textAlign: 'center',
                                            backgroundColor: '#f8f9fa',
                                            color: '#666',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        —
                                    </td>
                                </tr>
                            </tbody>
                        </Table>
                    </div>
                )}
                </>
                ) : null}

                <Modal show={showHistoryModal} onHide={this.closePartHistory} size="xl" centered>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            Transaction History - Part No: {historyPartNo}
                            {historyScopeLabel ? ` | ${historyScopeLabel}` : historyLocationCode ? ` | Location: ${historyLocationCode}` : ''}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {historyLoading ? (
                            <div style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>
                        ) : historyRows.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '20px' }}>No transaction history found.</div>
                        ) : (
                            <div className={SHARED_TABLE_SCROLL_WRAP_CLASS} style={{ maxHeight: '60vh' }}>
                                <Table bordered striped hover responsive>
                                    <thead>
                                        <tr>
                                            <th>No</th>
                                            <th>Transaction Type</th>
                                            <th>Transaction ID</th>
                                            <th>From</th>
                                            <th>To</th>
                                            {hasHistoryLocationScope && <th>In Qty</th>}
                                            {hasHistoryLocationScope && <th>Out Qty</th>}
                                            <th>Qty</th>
                                            {hasHistoryLocationScope && <th>Running Qty</th>}
                                            <th>Date</th>
                                            <th>Time</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {historyRows.map((row, index) => (
                                            <tr
                                                key={row.key}
                                                className={this.state.selectedHistoryRowIndex === index ? SHARED_TABLE_ROW_SELECTED_CLASS : ''}
                                                onClick={() => this.setState((s) => ({
                                                    selectedHistoryRowIndex: s.selectedHistoryRowIndex === index ? null : index,
                                                }))}
                                            >
                                                <td style={{ textAlign: 'center' }}>{index + 1}</td>
                                                <td>{row.transactionType}</td>
                                                <td>{row.transactionId}</td>
                                                <td>{row.fromLocation}</td>
                                                <td>{row.toLocation}</td>
                                                {hasHistoryLocationScope && <td style={{ textAlign: 'right' }}>{Number(row.inQty || 0).toLocaleString()}</td>}
                                                {hasHistoryLocationScope && <td style={{ textAlign: 'right' }}>{Number(row.outQty || 0).toLocaleString()}</td>}
                                                <td style={{ textAlign: 'right' }}>{Number(row.qty || 0).toLocaleString()}</td>
                                                {hasHistoryLocationScope && <td style={{ textAlign: 'right', fontWeight: 600 }}>{Number(row.runningQty || 0).toLocaleString()}</td>}
                                                <td>{row.dateText}</td>
                                                <td>{row.timeText}</td>
                                            </tr>
                                        ))}
                                        {hasHistoryLocationScope && (
                                            <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                                                <td colSpan={hasHistoryLocationScope ? 8 : 6} style={{ textAlign: 'right' }}>
                                                    Final Quantity
                                                </td>
                                                <td style={{ textAlign: 'right', fontWeight: 700 }}>
                                                    {Number(historyFinalQty || 0).toLocaleString()}
                                                </td>
                                                <td colSpan={2}></td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={this.closePartHistory}>Close</Button>
                    </Modal.Footer>
                </Modal>
            </div>
        );
    }
}

export { InventoryStatusOverview };
